import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { hasUnsavedChanges } from './unsavedChanges';
import { useFocusTrap } from '../lib/useFocusTrap';

/*
  Two of the three unsaved-changes consumers, mounted once under the data router
  (in Layout). The third — PWA update deferral — lives in src/pwa.

    1. beforeunload: native warning on tab close / reload / external navigation.
    2. In-app block: a quiet-luxury confirm modal on react-router navigations
       (sidebar links, breadcrumb, back/forward) while a form is dirty.

  Forced sign-outs redirect via window.location.assign and first call
  suppressGuard(), so hasUnsavedChanges() returns false here and neither prompt
  fires — security logout is never blocked.
*/
export function UnsavedChangesGuard() {
  // 1. Tab close / reload / typed-URL navigation.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        // Legacy browsers require returnValue to be set; the string is ignored.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // 2. In-app react-router navigation. Block only when actually changing path.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges() && currentLocation.pathname !== nextLocation.pathname,
  );

  const leaveBtnRef = useRef<HTMLButtonElement>(null);
  const blocked = blocker.state === 'blocked';

  // Traps Tab within the dialog (PS2) — declared before the "focus the
  // primary action" effect below so that effect's more specific target wins
  // (effects run in declaration order on mount).
  const trapRef = useFocusTrap<HTMLDivElement>(blocked);

  useEffect(() => {
    if (blocked) leaveBtnRef.current?.focus();
  }, [blocked]);

  // Escape = stay (the safe choice).
  useEffect(() => {
    if (!blocked) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') blocker.reset?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [blocked, blocker]);

  if (!blocked) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="unsaved-title"
      aria-describedby="unsaved-desc"
      ref={trapRef}
    >
      <div className="bg-surface border border-line shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] px-10 py-9 w-[380px] text-center rounded-token">
        <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-bronze/10">
          <AlertTriangle size={20} strokeWidth={1.75} aria-hidden className="text-bronze" />
        </div>
        <h2 id="unsaved-title" className="font-display text-[22px] font-semibold text-navy m-0 mb-2">
          Unsaved changes
        </h2>
        <p id="unsaved-desc" className="text-[13px] text-mid m-0 mb-7 leading-relaxed font-sans">
          You have edits that haven&rsquo;t been saved. Leaving this page will discard them.
        </p>
        <div className="flex flex-col gap-2.5">
          <button
            ref={leaveBtnRef}
            onClick={() => blocker.proceed?.()}
            className="w-full px-6 py-[12px] bg-navy text-cream border-none rounded-token text-[11px] font-bold font-sans uppercase tracking-[0.12em] cursor-pointer"
          >
            Leave &amp; discard
          </button>
          <button
            onClick={() => blocker.reset?.()}
            className="w-full px-6 py-[11px] bg-transparent text-slate border-none text-[11px] font-semibold font-sans uppercase tracking-[0.1em] cursor-pointer"
          >
            Stay on page
          </button>
        </div>
      </div>
    </div>
  );
}
